import { useState, useCallback } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { PageTransition } from './PageTransition'
import { CommandPalette } from './CommandPalette'
import { ShortcutsHelp } from './ShortcutsHelp'
import { OnboardingWizard, ONBOARDING_COMPLETE_KEY } from './OnboardingWizard'
import TopBar from './TopBar'
import { MobileNav } from './MobileNav'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { useAuth } from '@/hooks/useAuth'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'

// Sidebar quick-nav routes — 1-9 jump to the first 9 items
const SIDEBAR_ROUTES = [
  '/studio',
  '/',
  '/live',
  '/chat',
  '/analytics',
  '/alerts',
  '/highlights',
  '/clips',
  '/settings',
]

export function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const { user, isLoading } = useAuth()
  const [onboardingComplete] = useLocalStorage<boolean>(ONBOARDING_COMPLETE_KEY, false)
  const navigate = useNavigate()

  // Show the wizard when:
  //  - auth has finished loading (user is known)
  //  - user is authenticated
  //  - onboarding has not been completed/skipped yet
  const showWizard = !isLoading && user !== null && !onboardingComplete

  const openSidebar = () => setSidebarOpen(true)
  const closeSidebar = () => setSidebarOpen(false)
  const toggleSidebar = () => setSidebarOpen((v) => !v)

  const handleGoLive = useCallback(() => navigate('/live'), [navigate])
  const handleCreateClip = useCallback(() => navigate('/clips'), [navigate])
  const handleRunPoll = useCallback(() => navigate('/polls'), [navigate])
  const handleRunAd = useCallback(() => {
    // Placeholder: real implementation would dispatch via stream store
    navigate('/live')
  }, [navigate])
  const handleToggleMute = useCallback(() => {
    // Placeholder: dispatched to audio subsystem / scene-director
  }, [])
  const handleToggleBrb = useCallback(() => {
    // Placeholder: dispatched to scene-director
  }, [])
  const handleShowShortcuts = useCallback(() => setShortcutsOpen((v) => !v), [])

  // Sidebar digit shortcuts
  const sidebarHandlers = Object.fromEntries(
    SIDEBAR_ROUTES.map((route, i) => [
      `sidebar-${i + 1}`,
      () => navigate(route),
    ]),
  ) as Record<string, () => void>

  useKeyboardShortcuts(
    {
      'go-live': handleGoLive,
      'create-clip': handleCreateClip,
      'toggle-mute': handleToggleMute,
      'toggle-brb': handleToggleBrb,
      'run-poll': handleRunPoll,
      'run-ad': handleRunAd,
      'show-shortcuts': handleShowShortcuts,
      ...sidebarHandlers,
      // 'command-palette' is handled inside CommandPalette itself (Ctrl+K)
    },
    // Disable global shortcuts while the shortcuts help panel is open to avoid
    // triggering actions accidentally while reading the reference
    !shortcutsOpen,
  )

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {showWizard && <OnboardingWizard />}
      <CommandPalette />
      <ShortcutsHelp open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      {/* Slide-out sidebar — icon rail on md, full drawer on mobile */}
      <Sidebar mobileOpen={sidebarOpen} onMobileClose={closeSidebar} />

      <div className="flex flex-col flex-1 min-w-0">
        {/* TopBar: shows on all breakpoints. On mobile it renders the hamburger
            (passed via onHamburgerClick) that toggles the sidebar drawer. */}
        <TopBar onHamburgerClick={openSidebar} />

        {/* pb-[56px] clears the fixed MobileNav bar on mobile.
            md:pb-0 removes that clearance on desktop where MobileNav is hidden. */}
        <main
          className="flex-1 overflow-auto pb-[56px] md:pb-0"
          id="main-content"
          tabIndex={-1}
        >
          <PageTransition>
            <Outlet />
          </PageTransition>
        </main>
      </div>

      {/* Bottom tab bar — mobile only, floated above OS home indicator */}
      <MobileNav onHamburgerClick={toggleSidebar} />
    </div>
  )
}
